import { ColumnParams, CommonColumnParamsState } from "@org-avp/avp-avengers-ui-shared-utils";
import { createSlice, Dispatch, PayloadAction } from "@reduxjs/toolkit";
import { GetStateFn } from "../../utils/dispatchUtils";

export const tmmEditorViewOptionsSliceName = "tmmEditor";

export enum TmmEditorTabKey {
  SERIAL = "serial",
  INDIVIDUAL = "individual",
}

export enum TmmEditorSerialColumn {
  ADI = "ADI",
  COMMENT = "COMMENT",
  INDEX = "INDEX",
  LABEL = "LABEL",
  MATERIAL = "MATERIAL",
  RULE = "RULE",
  TRIGGER = "TRIGGER",
  TYPE = "TYPE",
  USE = "USE",
}

export enum TmmEditorIndividualColumn {
  COMMENT = "COMMENT",
  ID = "ID",
  ID_COLOR = "ID_COLOR",
  INDEX = "INDEX",
  LABEL = "LABEL",
  MATERIAL = "MATERIAL",
  RULE = "RULE",
  TRIGGER = "TRIGGER",
  TYPE = "TYPE",
  USE = "USE",
}

export const tmmEditorSerialColumnIds = [
  TmmEditorSerialColumn.ADI,
  TmmEditorSerialColumn.USE,
  TmmEditorSerialColumn.RULE,
  TmmEditorSerialColumn.TRIGGER,
  TmmEditorSerialColumn.MATERIAL,
  TmmEditorSerialColumn.LABEL,
  TmmEditorSerialColumn.INDEX,
  TmmEditorSerialColumn.TYPE,
  TmmEditorSerialColumn.COMMENT,
];

export const tmmEditorIndividualColumnIds = [
  TmmEditorIndividualColumn.USE,
  TmmEditorIndividualColumn.ID,
  TmmEditorIndividualColumn.RULE,
  TmmEditorIndividualColumn.TRIGGER,
  TmmEditorIndividualColumn.ID_COLOR,
  TmmEditorIndividualColumn.MATERIAL,
  TmmEditorIndividualColumn.LABEL,
  TmmEditorIndividualColumn.INDEX,
  TmmEditorIndividualColumn.TYPE,
  TmmEditorIndividualColumn.COMMENT,
];

export type TmmEditorViewOptionsState = {
  activeTabKey: TmmEditorTabKey;
  serialColumns: CommonColumnParamsState;
  individualColumns: CommonColumnParamsState;
};

const persistedTmmEditorViewOptions = localStorage.getItem(tmmEditorViewOptionsSliceName);
const persistedState = persistedTmmEditorViewOptions ? JSON.parse(persistedTmmEditorViewOptions) : null;

const fallback: TmmEditorViewOptionsState = {
  activeTabKey: TmmEditorTabKey.SERIAL,
  serialColumns: {
    columns: tmmEditorSerialColumnIds,
    params: Object.fromEntries(tmmEditorSerialColumnIds.map((columnId) => [columnId, { columnId: columnId }])),
  },
  individualColumns: {
    columns: tmmEditorIndividualColumnIds,
    params: Object.fromEntries(tmmEditorIndividualColumnIds.map((columnId) => [columnId, { columnId: columnId }])),
  },
};

const initialState: TmmEditorViewOptionsState = persistedState ?? fallback;

// if fields do not exist, set default fallback values
if (initialState.activeTabKey === undefined) initialState.activeTabKey = fallback.activeTabKey;
if (initialState.serialColumns === undefined) {
  initialState.serialColumns = fallback.serialColumns;
} else {
  if (initialState.serialColumns.columns.length === undefined || initialState.serialColumns.columns.length === 0) {
    initialState.serialColumns.columns = fallback.serialColumns.columns;
  }
  if (initialState.serialColumns.params === undefined)
    initialState.serialColumns.params = fallback.serialColumns.params;
}
if (initialState.individualColumns === undefined) {
  initialState.individualColumns = fallback.individualColumns;
} else {
  if (initialState.individualColumns.columns.length === undefined || initialState.individualColumns.columns.length === 0) {
    initialState.individualColumns.columns = fallback.individualColumns.columns;
  }
  if (initialState.individualColumns.params === undefined)
    initialState.individualColumns.params = fallback.individualColumns.params;
}

const tmmEditorViewOptionsSlice = createSlice({
  name: tmmEditorViewOptionsSliceName,
  initialState,
  reducers: {
    setLayoutProfileString(_, action: PayloadAction<string>) {
      return JSON.parse(action.payload);
    },
    setTmmEditorActiveTab(state: TmmEditorViewOptionsState, action: PayloadAction<TmmEditorTabKey>) {
      state.activeTabKey = action.payload;
    },
    setTmmEditorSerialColumnIds(state: TmmEditorViewOptionsState, action: PayloadAction<string[]>) {
      state.serialColumns.columns = action.payload;
    },
    updateSerialColumnWidth(state: TmmEditorViewOptionsState, action: PayloadAction<ColumnParams>) {
      const params = state.serialColumns.params ?? {};
      params[action.payload.columnId] = action.payload;
      state.serialColumns.params = params;
    },
    setTmmEditorIndividualColumnIds(state: TmmEditorViewOptionsState, action: PayloadAction<string[]>) {
      state.individualColumns.columns = action.payload;
    },
    updateIndividualColumnWidth(state: TmmEditorViewOptionsState, action: PayloadAction<ColumnParams>) {
      const params = state.individualColumns.params ?? {};
      params[action.payload.columnId] = action.payload;
      state.individualColumns.params = params;
    },
  },
});

export type TmmEditorViewOptionsRootState = { [tmmEditorViewOptionsSliceName]: TmmEditorViewOptionsState };
export const selectTmmEditorViewOptions = (state: TmmEditorViewOptionsRootState) => state[tmmEditorViewOptionsSliceName];
export const selectTmmEditorLayoutProfileString = (state: TmmEditorViewOptionsRootState) => JSON.stringify(state[tmmEditorViewOptionsSliceName]);
export const setTmmEditorLayoutProfileString = (layoutProfileString: string) => (dispatch: Dispatch, getState: GetStateFn) => {
  dispatch(tmmEditorViewOptionsSlice.actions.setLayoutProfileString(layoutProfileString));
  persistState(getState);
};
export const setTmmEditorActiveTabKey = (tabKey: TmmEditorTabKey) => (dispatch: Dispatch, getState: GetStateFn) => {
  dispatch(tmmEditorViewOptionsSlice.actions.setTmmEditorActiveTab(tabKey));
  persistState(getState);
};
export const setTmmEditorSerialColumns = (columns: string[]) => (dispatch: Dispatch, getState: GetStateFn) => {
  dispatch(tmmEditorViewOptionsSlice.actions.setTmmEditorSerialColumnIds(columns));
  persistState(getState);
};
export const updateTmmEditorSerialColumnWidth = (columnId: string, columnWidth?: number) => (dispatch: Dispatch, getState: GetStateFn) => {
  dispatch(tmmEditorViewOptionsSlice.actions.updateSerialColumnWidth({ columnId, width: columnWidth }));
  persistState(getState);
};
export const setTmmEditorIndividualColumns = (columns: string[]) => (dispatch: Dispatch, getState: GetStateFn) => {
  dispatch(tmmEditorViewOptionsSlice.actions.setTmmEditorIndividualColumnIds(columns));
  persistState(getState);
};
export const updateTmmEditorIndividualColumnWidth = (columnId: string, columnWidth?: number) => (dispatch: Dispatch, getState: GetStateFn) => {
  dispatch(tmmEditorViewOptionsSlice.actions.updateIndividualColumnWidth({ columnId, width: columnWidth }));
  persistState(getState);
};

function persistState(getState: GetStateFn) {
  localStorage.setItem(tmmEditorViewOptionsSliceName, JSON.stringify(
    selectTmmEditorViewOptions(getState()),
  ));
}

export const tmmEditorViewOptionsReducer = tmmEditorViewOptionsSlice.reducer;
